/*
  This example requires Tailwind CSS v2.0+

  This example requires some changes to your config:

  ```
  // tailwind.config.js
  module.exports = {
    // ...
    plugins: [
      // ...
      require('@tailwindcss/forms'),
    ],
  }
  ```
*/
import React, {Fragment, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Dialog, Transition} from "@headlessui/react";
import {XMarkIcon, CheckCircleIcon} from "@heroicons/react/24/outline";
import CircularProgress from "@mui/material/CircularProgress";
import Footer from "../../footer/Footer";
import HeaderNoLoginCheck from "../../header/HeaderNoLoginCheck";
import {useAuth0} from "@auth0/auth0-react";
import {venueSignup} from "../../requests/AppRequestPublic";

const EMPTY_SIGNUP = {
    venueName: "",
    contactName: "",
    contactPosition: "",
    contactPhone: "",
    contactEmail: ""
};

export default function LoginMobile() {

    const {loginWithRedirect} = useAuth0();
    const navigate = useNavigate();

    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(1);
    const [signup, setSignup] = useState(EMPTY_SIGNUP);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    useEffect(() => {
        // loginWithRedirect();
    }, []);

    const TOTAL_STEPS = 3;

    const update = (field, value) => {
        setSignup((s) => ({...s, [field]: value}));
    };

    const openWizard = () => {
        setSignup(EMPTY_SIGNUP);
        setStep(1);
        setSubmitted(false);
        setErrorMessage(null);
        setOpen(true);
    };

    const closeWizard = () => {
        setOpen(false);
    };

    const stepValid = () => {
        if (step === 1) {
            return signup.venueName.trim() !== "";
        }
        if (step === 2) {
            return signup.contactName.trim() !== "" && signup.contactPosition.trim() !== "";
        }
        if (step === 3) {
            return signup.contactEmail.trim() !== "" && signup.contactPhone.trim() !== "";
        }
        return false;
    };

    const next = () => {
        setErrorMessage(null);
        if (!stepValid()) {
            setErrorMessage("Please fill in the required fields.");
            return;
        }
        setStep((s) => Math.min(s + 1, TOTAL_STEPS));
    };

    const back = () => {
        setErrorMessage(null);
        setStep((s) => Math.max(s - 1, 1));
    };

    const submitSuccess = () => {
        setIsSubmitting(false);
        setSubmitted(true);
    };

    const submitError = () => {
        setIsSubmitting(false);
        setErrorMessage("Something went wrong submitting your signup. Please try again.");
    };

    const submit = () => {
        setErrorMessage(null);
        if (!stepValid()) {
            setErrorMessage("Please fill in the required fields.");
            return;
        }
        setIsSubmitting(true);
        venueSignup(signup, submitSuccess, submitError);
    };

    const inputClass =
        "mt-2 block w-full rounded-md border-0 py-2 px-3 text-gray-900 dark:text-gray-100 dark:bg-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-pink-600 sm:text-sm";
    const labelClass = "block text-left text-sm font-medium leading-6 text-gray-900 dark:text-gray-100";

    return (
        <>
            <HeaderNoLoginCheck showMenu={false}/>
            <div className="bg-white dark:bg-gray-900">
                <div className="px-6 py-24 sm:px-6 sm:py-32 lg:px-8">
                    <div className="mx-auto max-w-2xl text-center">
                        <h2 className="text-balance text-4xl font-semibold tracking-tight text-gray-900 dark:text-gray-100 sm:text-5xl">
                            Welcome to VirtualTipz
                        </h2>
                        <p className="mx-auto mt-6 max-w-xl text-pretty text-lg/8 text-gray-600 dark:text-gray-400">
                            Manage your venue with VirtualTipz.
                        </p>
                        <div className="mt-10 flex flex-col items-center justify-center gap-y-4">
                            <button
                                onClick={() => {
                                    loginWithRedirect();
                                }}
                                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-pink-500 hover:bg-pink-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-800"
                            >
                                Login
                            </button>
                            <button
                                onClick={openWizard}
                                className="w-full flex justify-center py-2 px-4 border border-pink-500 rounded-md shadow-sm text-sm font-medium text-pink-600 bg-white dark:bg-gray-800 hover:bg-pink-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
                            >
                                Sign Up Your Venue
                            </button>
                            <button
                                onClick={() => {
                                    navigate("/marketing");
                                }}
                                className="w-full flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
                            >
                                Marketing
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Signup wizard */}
            <Transition.Root show={open} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={closeWizard}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500/75 transition-opacity"/>
                    </Transition.Child>

                    <div className="fixed inset-0 z-10 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left shadow-xl transition-all">
                                    <div className="flex items-center justify-between">
                                        <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                            {submitted ? "Signup received" : "Sign up your venue"}
                                        </Dialog.Title>
                                        <button
                                            type="button"
                                            className="rounded-md text-gray-400 dark:text-gray-500 hover:text-gray-600"
                                            onClick={closeWizard}
                                        >
                                            <span className="sr-only">Close</span>
                                            <XMarkIcon className="h-6 w-6" aria-hidden="true"/>
                                        </button>
                                    </div>

                                    {submitted ? (
                                        <div className="mt-6 flex flex-col items-center text-center">
                                            <CheckCircleIcon className="h-12 w-12 text-green-500" aria-hidden="true"/>
                                            <p className="mt-4 text-base font-medium text-gray-900 dark:text-gray-100">
                                                Thanks, {signup.contactName || "there"}!
                                            </p>
                                            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                                We received your signup for {signup.venueName}. Our team will reach out
                                                soon to get {signup.venueName} set up on VirtualTipz.
                                            </p>
                                            <button
                                                type="button"
                                                onClick={closeWizard}
                                                className="mt-6 w-full rounded-md bg-pink-600 py-2 px-4 text-sm font-semibold text-white hover:bg-pink-500"
                                            >
                                                Done
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            {/* Progress */}
                                            <div className="mt-4 flex items-center gap-2">
                                                {[1, 2, 3].map((s) => (
                                                    <div
                                                        key={s}
                                                        className={
                                                            "h-1.5 flex-1 rounded-full " +
                                                            (s <= step ? "bg-pink-500" : "bg-gray-200 dark:bg-gray-700")
                                                        }
                                                    />
                                                ))}
                                            </div>
                                            <p className="mt-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                                                Step {step} of {TOTAL_STEPS}
                                            </p>

                                            <div className="mt-5 space-y-4">
                                                {step === 1 && (
                                                    <div>
                                                        <label htmlFor="venueName" className={labelClass}>
                                                            Venue name
                                                        </label>
                                                        <input
                                                            id="venueName"
                                                            type="text"
                                                            value={signup.venueName}
                                                            onChange={(e) => update("venueName", e.target.value)}
                                                            className={inputClass}
                                                            placeholder="e.g. Capital Cabaret"
                                                        />
                                                    </div>
                                                )}

                                                {step === 2 && (
                                                    <>
                                                        <div>
                                                            <label htmlFor="contactName" className={labelClass}>
                                                                Contact name
                                                            </label>
                                                            <input
                                                                id="contactName"
                                                                type="text"
                                                                value={signup.contactName}
                                                                onChange={(e) => update("contactName", e.target.value)}
                                                                className={inputClass}
                                                                placeholder="Your full name"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label htmlFor="contactPosition" className={labelClass}>
                                                                Position at venue
                                                            </label>
                                                            <input
                                                                id="contactPosition"
                                                                type="text"
                                                                value={signup.contactPosition}
                                                                onChange={(e) => update("contactPosition", e.target.value)}
                                                                className={inputClass}
                                                                placeholder="e.g. Owner, GM"
                                                            />
                                                        </div>
                                                    </>
                                                )}

                                                {step === 3 && (
                                                    <>
                                                        <div>
                                                            <label htmlFor="contactEmail" className={labelClass}>
                                                                Contact email
                                                            </label>
                                                            <input
                                                                id="contactEmail"
                                                                type="email"
                                                                value={signup.contactEmail}
                                                                onChange={(e) => update("contactEmail", e.target.value)}
                                                                className={inputClass}
                                                                placeholder="you@venue.com"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label htmlFor="contactPhone" className={labelClass}>
                                                                Contact number
                                                            </label>
                                                            <input
                                                                id="contactPhone"
                                                                type="tel"
                                                                value={signup.contactPhone}
                                                                onChange={(e) => update("contactPhone", e.target.value)}
                                                                className={inputClass}
                                                                placeholder="(555) 123-4567"
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                            </div>

                                            {errorMessage && (
                                                <p className="mt-3 text-sm text-red-600">{errorMessage}</p>
                                            )}

                                            <div className="mt-6 flex items-center justify-between gap-3">
                                                <button
                                                    type="button"
                                                    onClick={back}
                                                    disabled={step === 1 || isSubmitting}
                                                    className="rounded-md bg-white dark:bg-gray-800 px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40"
                                                >
                                                    Back
                                                </button>

                                                {step < TOTAL_STEPS ? (
                                                    <button
                                                        type="button"
                                                        onClick={next}
                                                        className="rounded-md bg-pink-600 px-5 py-2 text-sm font-semibold text-white hover:bg-pink-500"
                                                    >
                                                        Next
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={submit}
                                                        disabled={isSubmitting}
                                                        className="inline-flex items-center gap-2 rounded-md bg-pink-600 px-5 py-2 text-sm font-semibold text-white hover:bg-pink-500 disabled:opacity-60"
                                                    >
                                                        Submit
                                                        {isSubmitting && <CircularProgress color="inherit" size={16}/>}
                                                    </button>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>

            <Footer/>
        </>
    )
}
